import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import { LogDetail, LogDetailChangeType } from '../../../../../../../types/log.interface';
import { tryTranslate } from './i18n.utils';
import { ValueChangeComponent } from './value-change.component';

@Component({
  selector: 'ces-generic-detail',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ValueChangeComponent],
  templateUrl: './generic-detail.component.html',
})
export class GenericDetailComponent {
  private readonly translate = inject(TranslocoService);
  private readonly translations = toSignal(this.translate.selectTranslation());
  readonly detail = input.required<LogDetail>();

  private readonly entityKeyMap: Record<string, string> = {
    'campaign': 'history.entities.campaign',
    'input-data': 'history.entities.inputData',
    'file': 'history.entities.file',
  };

  private readonly attributeKeyMap: Record<string, string> = {
    name: 'history.fields.name',
    comment: 'history.fields.comment',
    operator: 'history.fields.operator',
    status: 'history.fields.status',
    value: 'history.fields.value',
    file: 'history.logContent.file',
    date: 'history.fields.date',
  };

  readonly entityName = computed(() => {
    this.translations();
    const d = this.detail();
    if (d.entityName) return tryTranslate(this.translate, d.entityName);
    const key = this.entityKeyMap[d.entity];
    return key ? this.translate.translate(key) : d.entity;
  });

  readonly attributeName = computed(() => {
    this.translations();
    const attribute = this.detail().attribute || '';
    const key = this.attributeKeyMap[attribute];
    return key ? this.translate.translate(key) : attribute;
  });

  readonly isEntityCreation = computed(() => {
    const d = this.detail();
    return d.type === LogDetailChangeType.CREATE && d.attribute === d.newValue;
  });

  readonly createdText = computed(() => {
    this.translations();
    return this.translate.translate('history.logContent.created');
  });
}
